import * as React from 'react';
import { DialogContent, DialogActions, Box } from '@mui/material';
import { FormControl, InputLabel, OutlinedInput } from '@mui/material';
import {
  FormWrapper,
  Typo,
  GeneralInfo,
  DeleteButton,
  SaveButton,
} from '../../styles';
import { useForm } from 'react-hook-form';
import { AddEducationModel } from '../../../../../../../interfaces/types/User';
import {
  useFieldsOfStudyQuery,
  useUniversityQuery,
} from '../../../../../../../rq/hooks/useQueries';
import { InputField } from '../../../../../InputField/InputField';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useDeleteEducation,
  useEditEducation,
} from '../../../../../../../rq/hooks/useEditProfile.mutation';
import { AddEducationSchema } from '../../../../../../../validators/EditProfile.validator';
import { months } from '../../monthYear';
import {
  BootstrapDialog,
  BootstrapDialogTitle,
} from '../../../../../../../../src/components/BootstrapDialog/BootstrapDialog';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { year } from '../../../../../../../static';
import Select from '../../../../../../../components/Select/Select';

export default function EditEducation({ field, open, setOpen }: any) {
  const { t } = useTranslation();
  const methods = useForm<AddEducationModel>({
    resolver: yupResolver(AddEducationSchema(t)),
  });
  const { data: universities = [] } = useUniversityQuery();
  const { data: fields_of_study = [] } = useFieldsOfStudyQuery();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: {
      errors: {
        description,
        educationId,
        fieldOfStudyId,
        startMonth,
        startYear,
      },
    },
  } = methods;

  React.useEffect(() => {
    if (field) {
      reset({ ...field });
    }
  }, [field, reset]);
  //   react-mutation
  const { mutate: editEducation, isLoading: editLoading } = useEditEducation(
    field?.candidateEducationId
  );

  const { mutate: deleteEducation, isLoading: deleteLoading } =
    useDeleteEducation({
      onSuccess: () => {
        setOpen(false);
        toast.success('Deletion was successfull');
        queryClient.fetchQuery(['profile']);
      },
    });

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = async (data: AddEducationModel) => {
    editEducation(data, {
      onSuccess: (res) => {
        setOpen(false);
        toast.success(t('successMessage.admin_verification'));
      },
    });
  };

  return (
    <div>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
      >
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <BootstrapDialogTitle
            id="customized-dialog-title"
            onClose={handleClose}
          >
            {t('editProfile.edit_candidate_profile.education.title_edit')}
          </BootstrapDialogTitle>
          <DialogContent dividers>
            <FormWrapper>
              <div>
                <Typo>
                  {t('editProfile.edit_candidate_profile.general_info')}
                </Typo>
                <GeneralInfo>
                  <FormControl fullWidth size="small" error={!!educationId}>
                    <InputLabel id="test-select-label">
                      {t(
                        'editProfile.edit_candidate_profile.education.university'
                      )}
                    </InputLabel>
                    <Select
                      defaultValue={field?.educationId ?? ''}
                      options={universities}
                      variant="outlined"
                      input={
                        <OutlinedInput
                          label={t(
                            'editProfile.edit_candidate_profile.education.university'
                          )}
                        />
                      }
                      {...register('educationId')}
                      fieldError={educationId}
                    />
                  </FormControl>
                  <FormControl fullWidth size="small" error={!!fieldOfStudyId}>
                    <InputLabel id="test-select-label">Orientation</InputLabel>
                    <Select
                      defaultValue={field?.fieldOfStudyId ?? ''}
                      options={fields_of_study}
                      variant="outlined"
                      input={<OutlinedInput label="Orientation" />}
                      {...register('fieldOfStudyId')}
                      fieldError={fieldOfStudyId}
                    />
                  </FormControl>
                </GeneralInfo>
              </div>
              <div>
                <Typo>
                  {t('editProfile.edit_candidate_profile.start_date')}
                </Typo>
                <GeneralInfo>
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{ mr: 1 }}
                    error={!!startMonth}
                  >
                    <InputLabel id="test-select-label">
                      {t('editProfile.edit_candidate_profile.month')}
                    </InputLabel>
                    <Select
                      defaultValue={field?.startMonth ?? ''}
                      options={months}
                      variant="outlined"
                      input={
                        <OutlinedInput
                          label={t('editProfile.edit_candidate_profile.month')}
                        />
                      }
                      {...register('startMonth')}
                      fieldError={startMonth}
                    />
                  </FormControl>

                  <FormControl
                    fullWidth
                    size="small"
                    sx={{ mr: 1 }}
                    error={!!startYear}
                  >
                    <InputLabel id="test-select-label">
                      {t('editProfile.edit_candidate_profile.year')}
                    </InputLabel>
                    <Select
                      defaultValue={field?.startYear ?? ''}
                      options={year()}
                      variant="outlined"
                      input={
                        <OutlinedInput
                          label={t('editProfile.edit_candidate_profile.year')}
                        />
                      }
                      {...register('startYear')}
                      fieldError={startYear}
                    />
                  </FormControl>
                </GeneralInfo>
              </div>
              <div>
                <Typo>{t('editProfile.edit_candidate_profile.end_date')}</Typo>
                <GeneralInfo>
                  <FormControl fullWidth size="small" sx={{ mr: 1 }}>
                    <InputLabel id="test-select-label">
                      {t('editProfile.edit_candidate_profile.month')}
                    </InputLabel>
                    <Select
                      defaultValue={field?.endMonth ?? ''}
                      options={months}
                      variant="outlined"
                      input={
                        <OutlinedInput
                          label={t('editProfile.edit_candidate_profile.month')}
                        />
                      }
                      {...register('endMonth')}
                    />
                  </FormControl>
                  <FormControl fullWidth size="small" sx={{ mr: 1 }}>
                    <InputLabel id="test-select-label">
                      {t('editProfile.edit_candidate_profile.year')}
                    </InputLabel>
                    <Select
                      defaultValue={field?.endYear ?? ''}
                      options={year()}
                      variant="outlined"
                      input={
                        <OutlinedInput
                          label={t('editProfile.edit_candidate_profile.year')}
                        />
                      }
                      {...register('endYear')}
                    />
                  </FormControl>
                </GeneralInfo>
              </div>
              <Typo>{t('editProfile.edit_candidate_profile.description')}</Typo>
              <InputField
                fullWidth
                size="small"
                fieldError={description}
                defaultValue={field?.description}
                {...register('description')}
              />
            </FormWrapper>
          </DialogContent>
          <DialogActions>
            <DeleteButton
              onClick={() => {
                deleteEducation(field?.candidateEducationId);
              }}
              variant="contained"
              loading={deleteLoading}
              autoFocus
            >
              {t('editProfile.edit_candidate_profile.delete')}
            </DeleteButton>
            <SaveButton
              loading={editLoading}
              variant="contained"
              type="submit"
              autoFocus
            >
              {t('editProfile.edit_candidate_profile.save')}
            </SaveButton>
          </DialogActions>
        </Box>
      </BootstrapDialog>
    </div>
  );
}
